// src/App.jsx

import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ConfigProvider, theme } from "antd";

import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AppLayout from "./components/AppLayout";
import Login from "./pages/Login";

// ✅ Core Pages
import Dashboard from "./pages/CaseManagement/Dashboard/Dashboard";
import CourtWiseShow from "./pages/CaseManagement/Dashboard/CourtWiseShow";
import CaseNotification from "./pages/CaseManagement/Dashboard/CaseNotification";
import UserManagement from "./pages/UserManagement";
import RoleManagement from "./pages/RoleManagement";
import EmployeeManagement from "./pages/EmployeeManagement";

// ✅ CaseManagement নতুন path
import HighCourtCaseEntry from "./pages/CaseManagement/HighCourt/HighCourtCaseEntry/HighCourtCaseEntry";
import AppelDivisionEntry from "./pages/CaseManagement/AppelDivision/AppelDivisionEntry/AppelDivisionEntry";
import AppealCourtCaseUpdateForm from "./pages/CaseManagement/AppelDivision/AppealCourtCaseUpdateForm/AppealCourtCaseUpdateForm";
import OtherCourtEntry from "./pages/CaseManagement/components/DistrictCourt/OtherCourtEntry";
import ThreeMemberTribunal from "./pages/CaseManagement/ThreeMemberTribunal/ThreeMemberTribunal";

// // Reports
import AllCaseReport from "./pages/CaseManagement/Reports/DistrictCourtReport/AllCaseReport";
import RunningCaseReport from "./pages/CaseManagement/Reports/DistrictCourtReport/RunningCaseReport";
import CloseCaseReport from "./pages/CaseManagement/Reports/DistrictCourtReport/CloseCaseReport";

import AllHighCaseReport from "./pages/CaseManagement/Reports/HighCourtReport/AllHighCaseReport";
import RunningHighCaseReport from "./pages/CaseManagement/Reports/HighCourtReport/RunningHighCaseReport";
import CloseHighCaseReport from "./pages/CaseManagement/Reports/HighCourtReport/CloseHighCaseReport";

import AllAppealReport from "./pages/CaseManagement/Reports/AppealCourtReport/AllAppealReport";
import RunningAppealCaseReport from "./pages/CaseManagement/Reports/AppealCourtReport/RunningAppealCaseReport";
import CloseAppealCaseReport from "./pages/CaseManagement/Reports/AppealCourtReport/CloseAppealCaseReport";
import SingleAppealCaseView from "./pages/CaseManagement/Reports/AppealCourtReport/SingleAppealCaseView";

import AllTribunalCaseReport from "./pages/CaseManagement/Reports/TribunalCourtReport/AllTribunalCaseReport";
import RunningTribunalCaseReport from "./pages/CaseManagement/Reports/TribunalCourtReport/RunningTribunalCaseReport";
import CloseTribunalCaseReport from "./pages/CaseManagement/Reports/TribunalCourtReport/CloseTribunalCaseReport";

// Other modules
import ImportantCase from "./pages/CaseManagement/ImportantCase/ImportantCase";

import LawAdvicer from "./pages/CaseManagement/LawAndPanelLawyer/LawAdvicer";
import PanelLawyer from "./pages/CaseManagement/LawAndPanelLawyer/PanelLawyer";
import LawyerEntry from "./pages/CaseManagement/LawAndPanelLawyer/LawyerEntry";

import CaseSummaryEntry from "./pages/CaseManagement/CaseSummary/CaseSummaryEntry";
import CaseSummaryReport from "./pages/CaseManagement/CaseSummary/CaseSummaryReport";
import CaseSummaryView from "./pages/CaseManagement/CaseSummary/CaseSummaryView";

// Settings
import CaseCategory from "./pages/CaseManagement/Setting/CaseCategory/CaseCategory";
import CaseType from "./pages/CaseManagement/Setting/CaseType/CaseType";
import JusticeName from "./pages/CaseManagement/Setting/JusticeName/JusticeName";
import RelatedLawOfficer from "./pages/CaseManagement/Setting/RelatedLawOfficer/RelatedLawOfficer";
import RelatedLawOfficerName from "./pages/CaseManagement/Setting/RelatedLawOfficerName/RelatedLawOfficerName";
import RespondentList from "./pages/CaseManagement/Setting/RespondentList/RespondentList";
import SingleCaseView from "./pages/CaseManagement/Reports/DistrictCourtReport/SingleCaseView";
import DistrictCourtCaseUpdateForm from "./pages/CaseManagement/DistrictCourt/DistrictCourtCaseUpdateForm/DistrictCourtCaseUpdateForm";
import FinalResult from "./pages/CaseManagement/Reports/DistrictCourtReport/FinalResult";
import SingleTribunalCaseView from "./pages/CaseManagement/Reports/TribunalCourtReport/SingleTribunalCaseView";
import ThreeMemberCaseUpdateForm from "./pages/CaseManagement/ThreeMemberTribunal/ThreeMemberCaseUpdateForm";
import SingleHighCaseView from "./pages/CaseManagement/Reports/HighCourtReport/SingleHighCaseView";
import HighCourtCaseUpdateForm from "./pages/CaseManagement/HighCourt/HighCourtCaseUpdateForm/HighCourtCaseUpdateForm";
import Profile from "./pages/Profile";

const antTheme = {
  algorithm: theme.defaultAlgorithm,
  token: {
    colorPrimary: "#4f46e5",
  },
};

const App = () => (
  <ConfigProvider theme={antTheme}>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />

          {/* Protected */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            {/* Dashboard */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/profile" element={<Profile />} />

            {/* Supreme Court */}
            <Route
              path="/high-court-case-entry"
              element={<HighCourtCaseEntry />}
            />
            <Route
              path="/appel-division-entry"
              element={<AppelDivisionEntry />}
            />

            {/* Tribunal */}
            <Route
              path="/three-member-tribunal"
              element={<ThreeMemberTribunal />}
            />

            {/* Other courts */}
            <Route path="/other-court-entry" element={<OtherCourtEntry />} />

            {/* Reports */}
            <Route
              path="/report/district-court/all-case"
              element={<AllCaseReport />}
            />
            <Route
              path="/report/district-court/running-case"
              element={<RunningCaseReport />}
            />
            <Route
              path="/district-court-case-update/:caseId"
              element={<DistrictCourtCaseUpdateForm />}
            />
            <Route
              path="/report/district-court/close-case"
              element={<CloseCaseReport />}
            />
            <Route
              path="/report/single-case/view/:entry_id"
              element={<SingleCaseView />}
            />
            <Route
              path="/report/high-court/all-case"
              element={<AllHighCaseReport />}
            />
            <Route
              path="/report/high-court/running-case"
              element={<RunningHighCaseReport />}
            />
            <Route
              path="/report/single-high-case/view/:entry_id"
              element={<SingleHighCaseView />}
            />

            <Route
              path="/report/high-court/close-case"
              element={<CloseHighCaseReport />}
            />
            <Route
              path="/high-court-case-update/:caseId"
              element={<HighCourtCaseUpdateForm />}
            />

            <Route path="/final-result/:entry_id" element={<FinalResult />} />
            <Route
              path="/report/appeal-court/running-case"
              element={<RunningAppealCaseReport />}
            />
            <Route
              path="/report/appeal-court/close-case"
              element={<CloseAppealCaseReport />}
            />

            <Route
              path="/report/tribunal-court/all-case"
              element={<AllTribunalCaseReport />}
            />
            <Route
              path="/report/tribunal-court/running-case"
              element={<RunningTribunalCaseReport />}
            />
            <Route
              path="/report/tribunal-court/close-case"
              element={<CloseTribunalCaseReport />}
            />
            <Route
              path="/report/single-tribunal-case/view/:entry_id"
              element={<SingleTribunalCaseView />}
            />
            <Route
              path="/tribunal-court-case-update/:caseId"
              element={<ThreeMemberCaseUpdateForm />}
            />

            <Route
              path="/report/appeal-court/all-case"
              element={<AllAppealReport />}
            />
            <Route
              path="/report/single-appeal-case/view/:entry_id"
              element={<SingleAppealCaseView />}
            />
            <Route
              path="/appeal-court-case-update/:caseId"
              element={<AppealCourtCaseUpdateForm />}
            />
            <Route
              path="/courtwise-report/:court_id"
              element={<CourtWiseShow />}
            />
            <Route path="/case/notifications" element={<CaseNotification />} />
            {/* Important */}
            <Route path="/important-running-case" element={<ImportantCase />} />

            {/* Lawyer */}
            <Route path="/lawadvicer" element={<LawAdvicer />} />
            <Route path="/panellawyer" element={<PanelLawyer />} />
            <Route path="/lawyer-entry" element={<LawyerEntry />} />

            {/* Case Summary */}
            <Route path="/case-summary-entry" element={<CaseSummaryEntry />} />
            <Route
              path="/case-summary-report"
              element={<CaseSummaryReport />}
            />
            <Route
              path="/case-summary-view/:case_id"
              element={<CaseSummaryView />}
            />

            {/* Settings */}
            <Route path="/setting/case-category" element={<CaseCategory />} />
            <Route path="/setting/case-type" element={<CaseType />} />
            <Route path="/setting/justice-name" element={<JusticeName />} />

            <Route
              path="/setting/related-law-officer"
              element={<RelatedLawOfficer />}
            />
            <Route
              path="/setting/related-law-officer-name"
              element={<RelatedLawOfficerName />}
            />
            <Route
              path="/setting/respondent-list"
              element={<RespondentList />}
            />

            {/* Admin */}
            <Route path="/users" element={<UserManagement />} />
            <Route path="/admin/roles" element={<RoleManagement />} />
            <Route path="/admin/employees" element={<EmployeeManagement />} />
          </Route>

          {/* Redirect */}
          <Route path="/" element={<Navigate to="/dashboard" />} />
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  </ConfigProvider>
);

export default App;
